'use client';

import { useState, useCallback } from 'react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';

import { capturePosthogEvent } from '@/lib/posthog';
import { sliderColorAt } from '@/lib/slider-color';

// Did the quiz actually place this person where they see themselves?
//
// Nothing else can answer that. A wrong label shares as readily as a right
// one, so saves, time on page and referrals look identical either way. Every
// rating is stamped with MATCHER_VERSION server-side, so a change to the
// matcher splits the ratings into a clean before and after.
//
// It used to be a card of its own: a full-width question, a sentence of
// justification, end labels and a Send button. That is a lot of the results
// page spent on a question the reader did not ask, so it is now a strip under
// the share buttons: one word, two icons, a track. It submits when the slider
// is released rather than on a button, and the API upserts, so changing your
// mind replaces the rating instead of adding one.

export default function ResultSatisfaction({
  quizResponseId,
  quizType,
  resultLabel,
  className = '',
}: {
  quizResponseId?: string;
  quizType: string;
  resultLabel: string;
  className?: string;
}) {
  const posthog = usePostHog();
  const [score, setScore] = useState(60);
  const [touched, setTouched] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = useCallback(async () => {
    if (!quizResponseId || !touched || sent) return;
    setSent(true);
    try {
      const res = await fetch('/api/quiz/satisfaction', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quizResponseId, score }),
      });
      if (!res.ok) throw new Error(String(res.status));
      capturePosthogEvent(posthog, 'result_satisfaction_submitted', {
        score,
        quiz_type: quizType,
        result_label: resultLabel,
      });
    } catch {
      // Let them try again rather than showing a thank-you for nothing.
      setSent(false);
    }
  }, [posthog, quizResponseId, quizType, resultLabel, score, sent, touched]);

  // Without a saved response there is nothing to attach a rating to, and an
  // unattached rating cannot be compared across matcher versions.
  if (!quizResponseId) return null;

  return (
    <div className={`mt-3 pt-3 border-t border-border ${className}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-foreground/45">
          {sent ? 'Thanks' : 'Happy?'}
        </span>
        {touched && (
          <span className="text-xs font-semibold tabular-nums text-text-secondary">{score}%</span>
        )}
      </div>

      <div className="flex items-center gap-2.5">
        <ThumbsDown size={15} className="text-text-muted shrink-0" aria-hidden />
        <div className="relative flex-1 h-5 select-none">
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-slate-200">
            <div
              className="absolute h-full left-0 rounded-full transition-[width,background-color] duration-100"
              style={{ width: `${score}%`, backgroundColor: sliderColorAt(score / 100) }}
            />
          </div>
          <div
            className="absolute top-1/2 w-4 h-4 bg-slate-600 rounded-full border-2 border-white shadow pointer-events-none transition-[left] duration-100"
            style={{
              left: `${score}%`,
              transform: 'translateX(-50%) translateY(-50%)',
              WebkitTransform: 'translateX(-50%) translateY(-50%)',
              // The same nudge the quiz gives an unanswered slider.
              animation: touched ? 'none' : 'pulse-scale 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              WebkitTapHighlightColor: 'transparent',
              opacity: sent ? 0.55 : 1,
            }}
          />
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={score}
            disabled={sent}
            aria-label={`How well ${resultLabel} matches how you see yourself, 0 to 100 percent`}
            onChange={(e) => { setScore(Number(e.target.value)); setTouched(true); }}
            // Submitted on release rather than on a button: one fewer control
            // on a strip whose whole point is not taking up room.
            onPointerUp={submit}
            onTouchEnd={submit}
            onKeyUp={submit}
            onBlur={submit}
            className="absolute inset-0 w-full h-full opacity-0 cursor-grab active:cursor-grabbing disabled:cursor-default"
          />
        </div>
        <ThumbsUp size={15} className="text-text-muted shrink-0" aria-hidden />
      </div>
    </div>
  );
}
